// noinspection SpellCheckingInspection

import {BaseStage} from "./BaseStage";
import {loading, stopLoading} from "../../../helpers/loading";
import Axios from "axios";
import handleAjaxErrorV2 from "../../../helpers/handleAjaxErrorV2";
import {waitUntil} from "../../../helpers/waitUntil";
import Panzoom from "@panzoom/panzoom";
import createElement from "../../../helpers/createElement";

class ReservedSeatsSelectStage extends BaseStage {
    /** @var {HTMLElement} */
    templatesContainer;

    /** @var {HTMLElement} */
    stageWrapper;

    /** @var {HTMLElement} */
    seatMapContainer;
    /** @var {HTMLElement} */
    seatMapWrapper;
    /** @var {HTMLElement} */
    selectedSeatsIndicator;
    /** @var {HTMLIFrameElement} */
    legacyMapIframe;

    animationHookActive = false;

    /** @var {ReservedSeatsSelectStageDefs.HandicapSeatDefinition[]} */
    handicapSeats;

    constructor(globalState) {
        super(globalState);

        // Init dom container
        this.domContainer = document.querySelector(`.stage.${this.getStageKey()}`)

        // Retrieve template container
        this.templatesContainer = document.querySelector('.templates .reserved-seats-select-stage-template');

        // Clone template
        this.stageWrapper = this.templatesContainer.querySelector('.stage-wrapper').cloneNode(true);

        this.seatMapContainer = this.stageWrapper.querySelector('.seat-map-container');
        this.seatMapWrapper = this.stageWrapper.querySelector('.seat-map-wrapper');
        this.selectedSeatsIndicator = this.stageWrapper.querySelector('.selected-seats-indicator');

        // Append stage to DOM
        this.domContainer.appendChild(this.stageWrapper);

        // Run base class initializations
        this.initSharedComponents();

        this.getSeatMapHtml();
    }

    getSeatMapHtml() {
        loading();

        Axios.post(this.globalState.selectedDate.getSeatMapRoute)
            .then(response => {
                // Retrieve map container with Iframe for legacy map
                this.seatMapContainer.innerHTML = response.data.map;

                this.retrieveLegacyMapFromIframe();
            })
            .catch(error => {
                handleAjaxErrorV2(error);
                stopLoading()
            })
    }

    retrieveLegacyMapFromIframe() {
        // Save iframe in memory
        this.legacyMapIframe = this.stageWrapper.querySelector('iframe');

        const state = this.globalState;

        // Wait until it has finished loading
        this.legacyMapIframe.addEventListener('load', () => {
            // Disable console logging
            // we don't want to log the iframe contents
            this.legacyMapIframe.contentWindow.console.log = () => {
            };

            const iframeDocument = this.legacyMapIframe.contentDocument;

            // Wait until parent event button is ready to be selected
            setTimeout(
                () => {
                    // Wait a bit to make sure everything has loaded
                    waitUntil(() => {
                        return iframeDocument.querySelector(`.btn_selectdate[data-id="${state.parentEvent.id}"]`)
                    })
                        .then(() => {
                            // Click parent event button to load dates
                            const eventButton = iframeDocument.querySelector(`.btn_selectdate[data-id="${state.parentEvent.id}"]`);

                            eventButton.click();

                            // Wait until dates are selectable
                            return waitUntil(() => {
                                return iframeDocument.querySelector(`.btn_selectseats[data-id="${state.selectedDate.id}"]`)
                            });
                        })
                        .then(() => {
                            // Click date event
                            const dateButton = iframeDocument.querySelector(`.btn_selectseats[data-id="${state.selectedDate.id}"]`);

                            dateButton.click();

                            // Wait until reserved seats button exists
                            return waitUntil(() => {
                                return iframeDocument.querySelector('.btn_selectseatsnext');
                            })
                        })
                        .then(() => {
                            // Click date event to retrieve map HTML
                            const reservedButton = iframeDocument.querySelector(`.btn_selectseatsnext`);

                            reservedButton.click();

                            // Wait until map container is loaded
                            return waitUntil(() => {
                                return iframeDocument.querySelector('#seats_container._loaded');
                            })
                        })
                        .then(() => {
                            // Hide everything but the map container
                            //// Hide the navbar
                            iframeDocument.querySelector('#header').classList.add('d-none');
                            //// Hide footer
                            iframeDocument.querySelector('#options').classList.add('d-none')

                            // Remove padding from body
                            iframeDocument.querySelector('body').style.padding = '0';

                            // Remove scroll bar from html node
                            iframeDocument.documentElement.style.overflow = 'hidden';

                            // Finally wait a bit...
                            setTimeout(() => {
                                // We need to create a new instance of panzoom that moves
                                // the whole iframe
                                const panzoomTarget = this.legacyMapIframe.parentElement;

                                const panzoomInstance = new Panzoom(
                                    panzoomTarget,
                                    {
                                        maxScale: 3,
                                        minScale: 0.2,
                                        startScale: 1,
                                        startX: 0,
                                        startY: 0,
                                    }
                                );

                                // Listen for events
                                panzoomTarget.parentElement.addEventListener('wheel', panzoomInstance.zoomWithWheel)

                                panzoomTarget.addEventListener('click', e => {
                                    this.handleIframeWrapperClick(e);
                                })

                                // Start on animation hook
                                this.onAnimationFrame();

                                // Set default zoom and pan based on screen size
                                setTimeout(() => {
                                    const screenWidth = window.innerWidth;

                                    let initialScale;
                                    let heightFactor;

                                    if (screenWidth < 600) {
                                        initialScale = 0.5;
                                        heightFactor = 4.5;
                                    } else if (screenWidth < 900) {
                                        initialScale = 0.5;
                                        heightFactor = 4.5;
                                    } else {
                                        initialScale = 0.5;
                                        heightFactor = 4.5;
                                    }

                                    const mapWidth = panzoomTarget.getBoundingClientRect().width;
                                    const mapHeight = panzoomTarget.getBoundingClientRect().height;

                                    const horizontalOffset = ((mapWidth / 2) - (screenWidth / 2)) * (1 / initialScale);
                                    const verticalOffset = ((mapHeight / heightFactor)) * (1 / initialScale);
                                    panzoomInstance.pan(-horizontalOffset, -verticalOffset,);

                                    //
                                    // // ... zoom out map ...
                                    panzoomInstance.zoom(initialScale);
                                }, 32)

                                // ... and make map visible and remove loading
                                this.legacyMapIframe.classList.remove('opacity-0')
                                stopLoading();

                                setTimeout(() => {
                                    this.initialMapTransformInsideIframe = panzoomTarget.style.transform;
                                    this.cleanUpZoomIndicator();
                                }, 2000)
                            }, 128)

                        })
                },
                128
            )
        })
    }

    /**
     * @return {PublicTicketsFormDefs.Stage}
     */
    getStageKey() {
        // noinspection JSValidateTypes
        return 'seat-select';
    }

    clearDom() {
        // Clear stage HTML
        this.domContainer.innerHTML = '';
    }

    handleLegacyMapClick() {
        // Get selected seats from map
        /** @var {PublicTicketsFormDefs.ReservedSeatSelection[]} */
        const seats = [];
        this.legacyMapIframe.contentDocument.querySelectorAll('.seat-selected').forEach(seatElement => {
            const parentRow = seatElement.closest('.seats-row')
            /** @var {PublicTicketsFormDefs.ReservedSection} */
            let section;
            /** @var {PublicTicketsFormDefs.SubSection} */
            let subSection;

            // Retrieve reserved section subsection from legacy map
            if (parentRow.classList.contains('seats-row-XA-lower')) {
                section = 'XA';
                subSection = 'lower';
            } else if (parentRow.classList.contains('seats-row-XA-upper')) {
                section = 'XA';
                subSection = 'upper';
            } else if (parentRow.classList.contains('seats-row-XB-lower')) {
                section = 'XB';
                subSection = 'lower';
            } else if (parentRow.classList.contains('seats-row-XB-upper')) {
                section = 'XB';
                subSection = 'upper';
            }

            seats.push({
                seat: seatElement.dataset.name,
                section,
                subSection,
            })
        })

        // Call event on new seats
        seats.forEach(newSeat => {
            // Compare with previous seats
            for (const oldSeat of this.globalState.selectedSeats) {
                if (oldSeat.seat === newSeat.seat) {
                    // Is not a new seat selection, skip
                    return;
                }
            }

            // New seat selection, call event
            this.onNewSeatSelection(newSeat);
        })

        // Update state
        this.globalState.setSelectedSeats(seats);

        this.updateSelectedSeatsIndicator();

        if (this.globalState.selectedSeats.length) {
            this.enableNextStageButton();
        } else {
            this.disableNextStageButton();
        }
    }

    updateSelectedSeatsIndicator() {
        const seatsCount = this.globalState.selectedSeats.length;

        this.selectedSeatsIndicator.innerHTML = `${seatsCount} ${seatsCount === 1 ? 'seat' : 'seats'} selected`;
    }

    /**
     * Called when a new seat has been selected
     *
     * @param {ReservedSeatSelection} selectedSeat
     */
    onNewSeatSelection(selectedSeat) {
        // Handle handicap seat tooltips
        const handicapSeats = this.getHandicapSeats()

        const iframeDocument = this.legacyMapIframe.contentDocument;

        handicapSeats.forEach(handicapSeatDef => {
            const wheelChairSeatElement = iframeDocument.querySelector(`.seat[data-name="${handicapSeatDef.wheelChairSeat}"]`);
            const companionSeatElement = iframeDocument.querySelector(`.seat[data-name="${handicapSeatDef.companionSeat}"]`);

            // Wheelchair seat selected
            if (selectedSeat.seat === handicapSeatDef.wheelChairSeat) {

                // Hide own tooltip if available
                if (handicapSeatDef.wheelChairSeatTooltipElement) {
                    handicapSeatDef.wheelChairSeatTooltipElement.removeAttribute('data-show');
                }

                // Show popup for companion seat if required and possible
                if (companionSeatElement.classList.contains('seat-available') && handicapSeatDef.companionSeatTooltipElement) {
                    // Display tooltip
                    handicapSeatDef.companionSeatTooltipElement.setAttribute('data-show', '');
                }
            }

            // If a wheelchair companion spot was selected
            if (selectedSeat.seat === handicapSeatDef.companionSeat) {
                // Hide own tooltip if available
                if (handicapSeatDef.companionSeatTooltipElement) {
                    handicapSeatDef.companionSeatTooltipElement.removeAttribute('data-show');
                }

                // Show popup for wheelchair seat if required and possible
                if (wheelChairSeatElement.classList.contains('seat-available') && handicapSeatDef.wheelChairSeatTooltipElement) {
                    // Display tooltip
                    handicapSeatDef.wheelChairSeatTooltipElement.setAttribute('data-show', '');
                }
            }
        })
    }

    cleanUpZoomIndicator() {
        // If stage is no longer part of dom
        if (document.contains(this.legacyMapIframe) === false) {
            // Stop checking
            return;
        }

        const mapTransform = this.legacyMapIframe.parentElement.style.transform;

        // If stage is no longer part of dom
        if (mapTransform !== this.initialMapTransformInsideIframe) {
            // Remove indicator
            this.domContainer.querySelector('.zoom-indicator').classList.add('opacity-0');
            // And stop checking
            return;
        }

        // And check again after a while
        setTimeout(() => {
            this.cleanUpZoomIndicator();
        }, 500)
    }

    /**
     * We need to redirect clicks from the wrapper to the
     * clicked element
     *
     * @param {MouseEvent} event
     */
    handleIframeWrapperClick(event) {
        const iframeDocument = this.legacyMapIframe.contentDocument;

        let clickScreenX = event.clientX;
        let clickScreenY = event.clientY;

        // Get transform position from iframe wrapper
        const iframeRect = this.legacyMapIframe.getBoundingClientRect();

        const transformString = this.legacyMapIframe.parentElement.style.transform;
        const transformScaleString = transformString.match(/scale\([\d.]+\)/)[0];

        let scale = 1;
        if (transformScaleString) {
            scale = parseFloat(transformScaleString.replace(/[^\d.]/g, ''));
        }

        // Get the relative position to the iframe container
        let clickRelativeToIframeX = clickScreenX - iframeRect.x;
        let clickRelativeToIframeY = clickScreenY - iframeRect.y;

        // Scale to match transform scale
        clickRelativeToIframeX /= scale;
        clickRelativeToIframeY /= scale;


        // Get all available seats
        iframeDocument.querySelectorAll('.seat-available,.seat-selected').forEach(seat => {
            const seatScreenRect = seat.getBoundingClientRect();

            const seatX = seatScreenRect.x;
            const seatRight = seatX + (seatScreenRect.width * 1.25);
            const seatY = seatScreenRect.y;
            const seatBottom = seatY + (seatScreenRect.height * 1.5);

            // Check if click is inside seat rect
            if (
                seatX <= clickRelativeToIframeX && seatRight >= clickRelativeToIframeX &&
                seatY <= clickRelativeToIframeY && seatBottom >= clickRelativeToIframeY
            ) {
                this.sendClickEventToLegacyMapSeat(seat)
            }
        })

    }

    /**
     * @param {HTMLElement} seatElement
     */
    sendClickEventToLegacyMapSeat(seatElement) {
        // Simulate click on element
        seatElement.dispatchEvent(new MouseEvent('click'));

        // Wait a bit so any internal behavior from the legacy map
        // has finished
        setTimeout(() => {
            this.handleLegacyMapClick()
        }, 8)
    }

    display() {
        super.display();

        if (this.animationHookActive === false) {
            // Re enable hook
            this.onAnimationFrame();
        }
    }

    /**
     * Called on each animation frame while the stage is active
     */
    onAnimationFrame() {
        this.animationHookActive = true;

        if (
            !this.domContainer || // If there is no container
            this.domContainer.innerHTML === '' || // Or has been cleared
            this.globalState.currentStage !== 'seat-select' // Or it is no longer at the seat stage
        ) {
            // Stage is gone, stop animating
            this.animationHookActive = false;
            return;
        }

        this.positionHandicapCompanionSeatsTooltipTargets();

        // Queue next frame
        requestAnimationFrame(() => {
            this.onAnimationFrame();
        })
    }

    /**
     * @param {HTMLElement} element
     * @return {DOMRect}
     */
    getPositionRelativeToIframeWrapper(element) {
        const dummyRect = new DOMRect(0, 0, 0, 0);

        if (!this.seatMapWrapper || !this.legacyMapIframe || !this.legacyMapIframe.contentDocument) {
            return dummyRect;
        }

        // Are we even pointing to a document inside the iframe?
        if (this.legacyMapIframe.contentDocument.contains(element) === false) {
            return dummyRect;
        }

        // We will first get the offset from the iframe, after the transformation has been applied
        // Get iframe offset
        const iframeBoundingRect = this.legacyMapIframe.getBoundingClientRect();
        // Get wrapper offset
        const wrapperOffset = this.seatMapWrapper.getBoundingClientRect();

        const offsetRelativeToWrapper = new DOMRect(
            iframeBoundingRect.x - wrapperOffset.x,
            iframeBoundingRect.y - wrapperOffset.y,
            5,
            5,
        );

        // Then we will get the offset from the target element, after scaling, relative to its parent
        // Retrieve panzoom transform
        const localCoordinatesRect = element.getBoundingClientRect();
        const transformString = this.legacyMapIframe.parentElement.style.transform;
        if (!transformString) {
            return dummyRect;
        }

        const transformScaleString = transformString.match(/scale\([\d.]+\)/)[0];
        // Get scale
        let transformScale = 1;
        if (transformScaleString) {
            transformScale = parseFloat(transformScaleString.replace(/[^\d.]/g, ''));
        }

        // Apply transform to local coordinates
        localCoordinatesRect.x *= transformScale;
        localCoordinatesRect.y *= transformScale;
        localCoordinatesRect.width *= transformScale;
        localCoordinatesRect.height *= transformScale;

        // Add local coordinates offsets after scaling to iframe offset relative to wrapper
        return new DOMRect(
            offsetRelativeToWrapper.x + localCoordinatesRect.x,
            offsetRelativeToWrapper.y + localCoordinatesRect.y,
            localCoordinatesRect.width,
            localCoordinatesRect.height,
        );
    }

    positionHandicapCompanionSeatsTooltipTargets() {
        if (!this.legacyMapIframe || !this.legacyMapIframe.contentDocument) {
            // End early
            return;
        }

        const iframeDocument = this.legacyMapIframe.contentDocument;

        const handicapSeats = this.getHandicapSeats();

        handicapSeats.forEach(handicapSeatDef => {
            // Check if both seats are available
            const wheelChairSeat = iframeDocument.querySelector(`.seat[data-name="${handicapSeatDef.wheelChairSeat}"]`);
            const companionSeat = iframeDocument.querySelector(`.seat[data-name="${handicapSeatDef.companionSeat}"]`);

            // Ignore if seats not found
            if (!wheelChairSeat || !companionSeat) {
                return;
            }

            // If seat is no longer a handicap seat as expected
            if (wheelChairSeat.classList.contains('seat-handicap') === false) {
                return;
            }

            // Handicap seats are available, setup tooltip target
            //// Create target element if missing
            const companionSeatTargetClassId = `tooltip-target-for-seat-${handicapSeatDef.companionSeat}`;
            const wheelChairSeatTargetClassId = `tooltip-target-for-seat-${handicapSeatDef.wheelChairSeat}`;

            let companionSeatTarget = this.seatMapWrapper.querySelector(`.${companionSeatTargetClassId}`);
            if (!companionSeatTarget) {
                // Create element
                companionSeatTarget = createElement(`
                    <div class="tooltip-target ${companionSeatTargetClassId}"></div>
                `)

                // And append to dom
                this.seatMapWrapper.appendChild(companionSeatTarget);

                // Create related tooltip element too
                if (window.Popper) {
                    const tooltipElement = createElement(`
                            <div class="alert alert-info m-0 p-2 handicap-tooltip handicap-companion-seat-popper-tooltip">
                                <div class="tooltip-body">
                                    <div class="text">
                                        You've chosen a wheelchair spot. <br>
                                        Select <b>campanion seat</b> also?
                                    </div>
                                    <div class="d-flex buttons">
                                        <button class="btn btn-sm btn-success yes-button flex-grow-1"><b>YES</b></button>
                                        <button class="btn btn-sm btn-light no-button ml-2 w-auto px-3">No</button>
                                    </div>
                                </div>
                                <div class="arrow" data-popper-arrow></div>
                            </div>
                        `);

                    tooltipElement.querySelector('.yes-button').addEventListener('click', () => {
                        this.sendClickEventToLegacyMapSeat(companionSeat)
                        // Hide tooltip
                        tooltipElement.removeAttribute('data-show', '');
                    })

                    tooltipElement.querySelector('.no-button').addEventListener('click', () => {
                        // Hide tooltip
                        tooltipElement.removeAttribute('data-show', '');
                    })

                    // Append to dom
                    this.seatMapWrapper.appendChild(tooltipElement);

                    // Save in memory
                    handicapSeatDef.companionSeatTooltipElement = tooltipElement;

                    // Init popper
                    handicapSeatDef.companionSeatPopperInstance = window.Popper.createPopper(
                        companionSeatTarget,
                        tooltipElement,
                        {
                            placement: 'top',
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 12],
                                    },
                                },
                            ],
                        },
                    );
                }
            }


            let wheelChairSeatTarget = this.seatMapWrapper.querySelector(`.${wheelChairSeatTargetClassId}`);
            if (!wheelChairSeatTarget) {
                // Create element
                wheelChairSeatTarget = createElement(`
                    <div class="tooltip-target ${wheelChairSeatTargetClassId}"></div>
                `)

                // And append to dom
                this.seatMapWrapper.appendChild(wheelChairSeatTarget);

                // Create related tooltip element too
                if (window.Popper) {
                    const tooltipElement = createElement(`
                            <div class="alert alert-info m-0 p-2 handicap-tooltip handicap-wheel-chair-seat-popper-tooltip">
                                <div class="tooltip-body">
                                    <div class="text">
                                        You've chosen a wheelchair companion spot. <br>
                                        Select <b>wheelchair seat</b> also?
                                    </div>
                                    <div class="d-flex buttons">
                                        <button class="btn btn-sm btn-success yes-button flex-grow-1"><b>YES</b></button>
                                        <button class="btn btn-sm btn-light no-button ml-2 w-auto px-3">No</button>
                                    </div>
                                </div>
                                <div class="arrow" data-popper-arrow></div>
                            </div>
                        `);

                    tooltipElement.querySelector('.yes-button').addEventListener('click', () => {
                        this.sendClickEventToLegacyMapSeat(wheelChairSeat)
                        // Hide tooltip
                        tooltipElement.removeAttribute('data-show', '');
                    })

                    tooltipElement.querySelector('.no-button').addEventListener('click', () => {
                        // Hide tooltip
                        tooltipElement.removeAttribute('data-show', '');
                    })

                    // Append to dom
                    this.seatMapWrapper.appendChild(tooltipElement);

                    // Save in memory
                    handicapSeatDef.wheelChairSeatTooltipElement = tooltipElement;

                    // Init popper
                    handicapSeatDef.wheelChairSeatPopperInstance = window.Popper.createPopper(
                        wheelChairSeatTarget,
                        tooltipElement,
                        {
                            placement: 'top',
                            modifiers: [
                                {
                                    name: 'offset',
                                    options: {
                                        offset: [0, 12],
                                    },
                                },
                            ],
                        },
                    );
                }
            }

            // Get seat position relative to wrapper
            const companionSeatRect = this.getPositionRelativeToIframeWrapper(companionSeat);
            const wheelChairSeatRect = this.getPositionRelativeToIframeWrapper(wheelChairSeat);

            // Position targets over seat
            companionSeatTarget.style.left = `${companionSeatRect.x}px`;
            companionSeatTarget.style.top = `${companionSeatRect.y}px`;
            companionSeatTarget.style.width = `${companionSeatRect.width}px`;
            companionSeatTarget.style.height = `${companionSeatRect.height}px`;

            wheelChairSeatTarget.style.left = `${wheelChairSeatRect.x}px`;
            wheelChairSeatTarget.style.top = `${wheelChairSeatRect.y}px`;
            wheelChairSeatTarget.style.width = `${wheelChairSeatRect.width}px`;
            wheelChairSeatTarget.style.height = `${wheelChairSeatRect.height}px`;

            // If we have a popper instances setup, update them too if they are visible
            if (handicapSeatDef.companionSeatPopperInstance && handicapSeatDef.companionSeatTooltipElement.hasAttribute('data-show')) {
                handicapSeatDef.companionSeatPopperInstance.update();
            }
            if (handicapSeatDef.wheelChairSeatPopperInstance && handicapSeatDef.wheelChairSeatTooltipElement.hasAttribute('data-show')) {
                handicapSeatDef.wheelChairSeatPopperInstance.update();
            }
        })
    }

    /**
     *
     * @return {ReservedSeatsSelectStageDefs.HandicapSeatDefinition[]}
     */
    getHandicapSeats() {
        // Create handicap seats definitios only once, as we store data in them too
        if (this.handicapSeats === undefined) {
            // Helper functions

            this.handicapSeats = [
                {
                    wheelChairSeat: 'DA-22-10',
                    companionSeat: 'DA-22-11',
                },
                {
                    wheelChairSeat: 'DA-22-9',
                    companionSeat: 'DA-22-8',
                },

                {
                    wheelChairSeat: 'CA-22-10',
                    companionSeat: 'CA-22-11',
                },
                {
                    wheelChairSeat: 'CA-22-9',
                    companionSeat: 'CA-22-8',
                },

                {
                    wheelChairSeat: 'BA-22-10',
                    companionSeat: 'BA-22-11',
                },
                {
                    wheelChairSeat: 'BA-22-9',
                    companionSeat: 'BA-22-8',
                },
            ];
        }

        return this.handicapSeats;
    }
}

export {ReservedSeatsSelectStage};
